import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, Loader2, TrendingUp, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ClickableSKU } from "@/components/drilldown/Clickables";
import { useDrillDown } from "@/components/drilldown/DrillDownContext";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import inventory from "@/api/inventory";
import inventoryPdf, { pdfErrorMessage } from "@/api/inventoryPdf";
import PdfExportButton from "./PdfExportButton";

function fmtNum(n) { return Number(n || 0).toLocaleString("en-US"); }
function fmtMoney(n) { return Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }); }

export default function InventoryTopProductsTable({
  filters,
  testId = "top-products-table",
  apiOverride,
  hideExport = false,
}) {
  const { t } = useTranslation();
  const { openProductModal } = useDrillDown();
  const api = apiOverride || inventory;
  const [metric, setMetric] = useState("qty");
  const [topN, setTopN] = useState(25);
  const [data, setData] = useState({ items: [], total_universe: 0, metric_used: "qty" });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .getTopProducts(filters, { metric, topN })
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => {
        if (!cancelled) {
          toast.error(t("inventory.errors.topProductsLoad", { defaultValue: "No se pudo cargar top productos" }));
          console.error(e);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filters, metric, topN, t]);

  const handleExport = async () => {
    try {
      setExporting(true);
      await api.downloadTopProductsExport(filters, { metric, topN });
      toast.success(t("inventory.export.success", { defaultValue: "Excel descargado" }));
    } catch (e) {
      toast.error(t("inventory.export.error", { defaultValue: "Fallo al exportar Excel" }));
      console.error(e);
    } finally { setExporting(false); }
  };

  const handleExportPdf = async () => {
    try {
      setExportingPdf(true);
      await inventoryPdf.topProducts(filters, { metric, top_n: topN });
      toast.success(t("inventory.exportPdf.success", { defaultValue: "PDF descargado" }));
    } catch (e) {
      toast.error(pdfErrorMessage(e, t));
      console.error(e);
    } finally { setExportingPdf(false); }
  };

  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Tabs value={metric} onValueChange={setMetric} data-testid="top-metric-tabs">
          <TabsList className="h-8">
            <TabsTrigger value="qty" className="text-xs" data-testid="metric-qty">
              <TrendingUp className="h-3 w-3 mr-1.5" />
              {t("inventory.topProducts.byQty", { defaultValue: "Por cantidad" })}
            </TabsTrigger>
            <TabsTrigger value="cost" className="text-xs" data-testid="metric-cost">
              <DollarSign className="h-3 w-3 mr-1.5" />
              {t("inventory.topProducts.byCost", { defaultValue: "Por costo" })}
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>Top N:</span>
          <Select value={String(topN)} onValueChange={(v) => setTopN(Number(v))}>
            <SelectTrigger className="h-8 w-[90px] text-sm" data-testid="top-n-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting || loading} data-testid="top-export-btn" className={hideExport ? "hidden" : ""}>
            {exporting ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}
            {t("inventory.exportExcel", { defaultValue: "Exportar Excel" })}
          </Button>
          <PdfExportButton
            onClick={handleExportPdf}
            exporting={exportingPdf}
            disabled={loading}
            hidden={hideExport}
            testId="top-pdf-btn"
          />
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs w-[60px] text-right">#</TableHead>
              <TableHead className="text-xs">SKU</TableHead>
              <TableHead className="text-xs">Descripción</TableHead>
              <TableHead className="text-xs text-right">Qty total</TableHead>
              <TableHead className="text-xs text-right">Costo total</TableHead>
              <TableHead className="text-xs text-right w-[180px]">% del total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && data.items.length === 0
              ? Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 6 }).map((__, j) => (
                      <TableCell key={j}><Skeleton className="h-3.5 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))
              : data.items.length === 0
                ? <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">{t("inventory.empty", { defaultValue: "Sin resultados" })}</TableCell></TableRow>
                : data.items.map((row) => (
                  <TableRow key={`${row.rank}-${row.sku}`} data-testid={`top-row-${row.sku}`}>
                    <TableCell className="text-right tabular-nums text-xs font-semibold">{row.rank}</TableCell>
                    <TableCell className="text-xs font-mono">
                      <ClickableSKU sku={row.sku} onOpen={openProductModal}>
                        {row.sku}
                      </ClickableSKU>
                    </TableCell>
                    <TableCell className="text-xs max-w-[240px] truncate">{row.description || "—"}</TableCell>
                    <TableCell className="text-right tabular-nums text-xs">{fmtNum(row.qty_total)}</TableCell>
                    <TableCell className="text-right tabular-nums text-xs">{fmtMoney(row.costo_total)}</TableCell>
                    <TableCell className="text-right text-xs">
                      <div className="flex items-center gap-2 justify-end">
                        <div className="w-[100px] bg-muted rounded-full h-2 overflow-hidden">
                          <div
                            className="h-full bg-primary"
                            style={{ width: `${Math.min(100, Math.max(2, row.pct_of_total))}%` }}
                            data-testid={`top-bar-${row.sku}`}
                          />
                        </div>
                        <span className="tabular-nums w-[55px] text-right">{row.pct_of_total}%</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </div>

      <div className="text-[11px] text-muted-foreground">
        {t("inventory.topProducts.universeLabel", { defaultValue: "Universo total" })}: {metric === "qty" ? fmtNum(data.total_universe) : fmtMoney(data.total_universe)}
      </div>
    </div>
  );
}
